import React from 'react';
import { Platform } from 'react-native';
import { createStackNavigator } from '@react-navigation/stack';
import { useAppSelector } from '@/store';

// Wrapped screens with new Figma header navigation
import { ResidentPaymentsScreen } from '@/screens/residents/payments/ResidentPaymentsScreen';
import { ResidentProfileScreen } from '@/screens/residents/profile/ResidentProfileScreen';
import { MessagesStackNavigator } from './MessagesStackNavigator';
import { ResidentLinkedAccountsScreen } from '@/screens/residents/linkedAccounts/ResidentLinkedAccountsScreen';
import { ResidentEventsScreen } from '@/screens/residents/events/ResidentEventsScreen';
import { ResidentNotificationsScreen } from '@/screens/residents/notifications/ResidentNotificationsScreen';
import { CommunityMarketplaceScreen } from '@/screens/residents/marketplace/CommunityMarketplaceScreen';
import { ResidentFAQScreen } from '@/screens/residents/faq/ResidentFAQScreen';
import { MobileAccountScreen } from '@/screens/residents/account/mobile/MobileAccountScreen';
import { MobileResourcesScreen } from '@/screens/residents/resources/mobile/MobileResourcesScreen';
import { MobileMaintenanceScreen } from '@/screens/residents/maintenance/mobile/MobileMaintenanceScreen';
import { ResidentHomeScreen } from '@/screens/residents/home/ResidentHomeScreen';
import { CommunityBoardScreen } from '@/screens/residents/corkBoard/CommunityBoardScreen';

export type ResidentMobileParamList = {
  ResidentHome: undefined;
  ResidentPayments: undefined;
  ResidentLinkedAccounts: undefined;
  ResidentMaintenance: undefined;
  ResidentEvents: undefined;
  ResidentMarketplace: undefined;
  ResidentCommunityBoard: undefined;
  ResidentResources: undefined;
  ResidentWifi: undefined;
  ResidentFaq: undefined;
  ResidentMessages: undefined;
  ResidentNotifications: undefined;
  ResidentProfile: undefined;
  ResidentAccountSettings: { section?: string } | undefined;
};

const Stack = createStackNavigator<ResidentMobileParamList>();

export const ResidentMobileNavigator: React.FC = () => {
  const { user } = useAppSelector((state) => state.auth);
  
  // Hide Payments for Non-Leasing Occupants
  const shouldShowPayments = user?.customerType !== 'non-leasing occupant';

  return (
    <Stack.Navigator
      initialRouteName="ResidentHome"
      screenOptions={{
        headerShown: false,
        // Disable animations on web to avoid fade on path navigation
        ...(Platform.OS === 'web'
          ? { animationEnabled: false as const }
          : {
              // Custom transition for seamless menu-to-screen experience (native only)
              cardStyleInterpolator: ({ current }) => ({
                cardStyle: {
                  opacity: current.progress,
                },
              }),
              transitionSpec: {
                open: {
                  animation: 'timing',
                  config: {
                    duration: 250,
                  },
                },
                close: {
                  animation: 'timing',
                  config: {
                    duration: 200,
                  },
                },
              },
            }),
      }}
    >
      <Stack.Screen name="ResidentHome" component={ResidentHomeScreen} />
      {shouldShowPayments && (
        <Stack.Screen name="ResidentPayments" component={ResidentPaymentsScreen} />
      )}
      <Stack.Screen name="ResidentLinkedAccounts" component={ResidentLinkedAccountsScreen} />
      <Stack.Screen name="ResidentMaintenance" component={MobileMaintenanceScreen} />
      <Stack.Screen name="ResidentEvents" component={ResidentEventsScreen} />
      <Stack.Screen name="ResidentMarketplace" component={CommunityMarketplaceScreen} />
      <Stack.Screen name="ResidentCommunityBoard" component={CommunityBoardScreen} />
      <Stack.Screen name="ResidentResources" component={MobileResourcesScreen} />
      <Stack.Screen name="ResidentFaq" component={ResidentFAQScreen} />
      <Stack.Screen name="ResidentMessages" component={MessagesStackNavigator} />
      <Stack.Screen name="ResidentNotifications" component={ResidentNotificationsScreen} />
      <Stack.Screen name="ResidentProfile" component={ResidentProfileScreen} />
      <Stack.Screen name="ResidentAccountSettings" component={MobileAccountScreen} />
    </Stack.Navigator>
  );
};

